/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, ElementRef, ViewChild} from '@angular/core';
import {IFilterAngularComp} from 'ag-grid-angular';
import {IAfterGuiAttachedParams, IDoesFilterPassParams, IFilterParams, RowNode} from "ag-grid-community";

@Component({
    selector: 'app-suggestion-filter',
    templateUrl: './autocomplete-filter.component.html',
    styleUrl: './autocomplete-filter.component.scss',
    standalone: false
})
export class AutocompleteFilterComponent implements IFilterAngularComp {
  @ViewChild('eFilterText') eFilterText!: ElementRef;
  availableOptions: any[] = [];
  filteredOptions: any[] = [];
  selectedOption: any = undefined;
  filterParams!: IFilterParams;
  colName = "";
  headerName = "";

  agInit(params: IFilterParams): void {
    this.filterParams = params;
    this.colName = this.filterParams.colDef.field ?? "";
    this.headerName = this.filterParams.colDef.headerName ?? "";
  }

  /**
   * Changes the options selected for the filter when the selection changes in the HTML
   * @param $event The event variable emitted by the multiselect
   */
  selectOptions($event: any): void {
    this.selectedOption = $event;
    this.filteredOptions = this.availableOptions.filter(value => value.toString().toLowerCase().includes(this.selectedOption.toString().toLowerCase()));
    this.onFilterChanged();
  }

  /**
   * returns true if the row is not filtered out, false if the row gets filtered out
   * Required for AG grid filter
   * @param params The parameters variable used by AG grid
   */
  doesFilterPass(params: IDoesFilterPassParams): boolean {
    const {node} = params;
    const value = this.filterParams.getValue(node);
    if (value &&
      (value.toString().toLowerCase().includes(this.selectedOption.toString().toLowerCase()) || params.data.floatingHeader)) {
      return true;
    }
    return false;
  }

  /**
   * Returns a boolean for whether the filter should be filtering or not
   */
  isFilterActive(): boolean {
    return this.selectedOption != null && this.selectedOption !== '';
  }

  /**
   *  Gets the filter state. If filter is not active, then should return null/undefined.
   */
  getModel() {
    if (!this.isFilterActive()) {
      return null;
    }
    return {value: this.selectedOption};
  }

  /**
   * Restores the filter state. Called by the grid after gridApi.setFilterModel(model) is called.
   * Required for AG grid filter
   * @param model
   */
  setModel(model: any) {
    this.selectedOption = model == null ? null : model.value;
  }

  /**
   * Calls the callback when the filter changes
   */
  onFilterChanged() {
    this.filterParams.filterChangedCallback();
  }

  /**
   * when the filter is opened, focuses on the text field
   * @param params The parameters generated by the GUI attached event
   */
  afterGuiAttached(params?: IAfterGuiAttachedParams): void {

    // Get the data for the autocomplete that passes all the other filters
    if (this.filterParams != null) {
      const tempArr: any[] = [];
      this.filterParams.rowModel.forEachNode((rowNode: RowNode) => {
        if (rowNode.data[this.colName] != undefined && !rowNode.data.floatingHeader && this.filterParams.doesRowPassOtherFilter(rowNode)) {
          tempArr.push(rowNode.data[this.colName]);
        }
      });
      this.availableOptions = [...new Set(tempArr)];
      this.filteredOptions = [...new Set(tempArr)];
    }

    if (!params?.suppressFocus) {
      // focus the input element for keyboard navigation
      this.eFilterText!.nativeElement.focus();
    }
  }
}
